import { VertexBufferObject } from './VertexBufferObject';
import { gl } from '../../Engine';
import { AttributeVertexBufferObject } from './AttributeVertexBufferObject';

/**
 * Vertex Array Object (VAO) is an object that encapsulates the configuration of vertex data
 * used during rendering. It serves as a container for all the state
 * necessary to specify how vertex attributes are organized and accessed by the GPU.
 *
 * VAO stores multiple {@link VertexBufferObject VertexBufferObject}s (VBOs) and their associated vertex attribute configurations.
 * VBO is a buffer that holds the actual vertex data, such as positions, normals, texture coordinates, etc.
 * Each VBO is associated with a specific attribute index, which represents a particular vertex attribute in the shader program.
 *
 * By binding a VAO, you can set up the state for drawing a specific object or scene.
 * The VAO remembers the connections between attribute indices and VBOs,
 * so when you bind the VAO, it automatically configures the pipeline to fetch the vertex attributes from the correct VBOs.
 *
 * Using a VAO has several benefits:
 * Simplified setup: Once you define the VAO configuration, you can reuse it across multiple render calls
 * without having to reconfigure the vertex attribute pointers each time.
 *
 * Improved performance: By organizing and binding vertex data through a VAO,
 * WebGL can optimize the data transfer and caching, resulting in better performance.
 *
 * Enhanced code readability: VAOs provide a clear separation between vertex data setup and rendering calls,
 * making the code more structured and easier to understand.
 */
export class VertexArrayObject {
    /**
     * Reference to {@link WebGLVertexArrayObject}.
     * It is important to remember to {@link delete} the buffer when no more needed.
     */
    protected readonly id: WebGLVertexArrayObject;

    /**
     * Map containing reference to all the {@link AttributeVertexBufferObject AttributeVertexBufferObject}s
     * of type {@link gl.ARRAY_BUFFER} inside this {@link VertexArrayObject}.
     * So this map only contains the {@link VertexBufferObject}s containing Vertex data.
     *
     * The key is the attribute-number of the {@link VertexBufferObject VertexBufferObject}.
     * For the first VBO the key should be 0, increasing for all subsequent VBOs.
     */
    protected vertexBufferObjects: Map<number, AttributeVertexBufferObject>;

    /**
     * {@link VertexBufferObject VertexBufferObject} of type {@link gl.ELEMENT_ARRAY_BUFFER} storing the index data of the mesh.
     * This is used in rendering calls of type {@link gl.drawElements()}.
     *
     * indexVBO tells the renderer the order in which the vertices should be connected when rendering the primitives.
     * For example index data of [0, 1, 2, 2, 1, 3] using primitives of type {@link gl.TRIANGLES} would render two triangles:
     * The first triangle would be made out of vertex data at indices 0, 1 and 2
     * and the second one would be made out of vertex data at indices 2, 1 and 3.
     *
     * The vertex data itself should be stored in {@link AttributeVertexBufferObject AttributeVertexBufferObjects},
     * and the index values in this VBO should refer to those indices.
     */
    private indexVBO?: VertexBufferObject;

    /**
     * Number of indices stored within the {@link this.indexVBO}.
     */
    private _indexCount: number;

    /**
     * Constructor that will generate a new {@link WebGLVertexArrayObject}
     * @throws if VertexArrayObject creation fails.
     * This can occur if system limit of VAOs is reached or WebGL memory runs out.
     */
    public constructor() {
        const id = gl.createVertexArray();
        if (!id) {
            throw new Error('VertexArrayObject creation failed');
        }
        this.id = id;
        this.vertexBufferObjects = new Map<
            number,
            AttributeVertexBufferObject
        >();
        this.indexVBO = undefined;
        this._indexCount = 0;
    }

    /**
     * Number of indices stored within the {@link indexVBO}.
     */
    public get indexCount(): number {
        return this._indexCount;
    }

    /**
     * Initializes the {@link indexVBO indexVertexBufferObject} with given array of index values.\
     * Indices-array should contain the index data starting with index value 0.\
     * Make sure to {@link bind() bind} this VAO before creating index buffer.
     *
     * @throws if indexVertexBufferObject is already defined.
     * @param indices Array of index values. First index should have value of 0.
     * @returns True to indicate that Index buffer creation was successful
     */
    public createIndexBuffer(indices: number[] | Int32Array): boolean {
        if (this.indexVBO) {
            throw new Error('Attempt to overwrite Index VertexBufferObject');
        }
        this.indexVBO = new VertexBufferObject(gl.ELEMENT_ARRAY_BUFFER);
        this.indexVBO.bind();
        const buffer = Array.isArray(indices)
            ? new Int32Array(indices)
            : indices;
        this.indexVBO.storeData(buffer as unknown as ArrayBuffer);
        this._indexCount = indices.length;
        return true;
    }

    /**
     * Method to create a new VertexBufferObject of type {@link gl.ARRAY_BUFFER} for an attribute.\
     * These types of attributes are used to store vertex attributes,
     * such as positions, normals and texture coordinates.\
     * This method creates the {@link AttributeVertexBufferObject},\
     * binds it so it can be modified,\
     * stores the provided data inside that buffer,\
     * links it to this {@link VertexArrayObject} and\
     * unbinds it.
     * Make sure to {@link bind() bind} this VAO before creating Attributes.
     *
     * @throws if VertexArrayObject already has binding for the given attribute or\
     * {@link data} is array of numbers and {@link AttributeVertexBufferObject.dataType dataType} is of unknown type.
     * @param attribute Attribute to which the created {@link VertexBufferObject} should be stored
     * @param dataType The data type of the stored data.\
     * The following data types are available:\
     * Data type                         internal format    value range\
     * {@link gl.BYTE}                          int8        [-128, 127]\
     * {@link gl.SHORT}                         int16       [-32768, 32767]\
     * {@link gl.UNSIGNED_BYTE}                 uint8       [0, 255]\
     * {@link gl.UNSIGNED_SHORT}                uint16      [0,65535]\
     * {@link gl.FLOAT}                         float32     floating point number\
     * {@link gl.HALF_FLOAT}                    float16     floating point number\
     * {@link gl.INT}                           int32       [-2147483648, 2147483647]\
     * {@link gl.UNSIGNED_INT}                  uint32      [0, 4294967295]\
     * {@link gl.INT_2_10_10_10_REV}            int32       [-512, 511]\
     * {@link gl.UNSIGNED_INT_2_10_10_10_REV}   uint32      [0, 1023]
     * @param data The data to be stored in that {@link VertexBufferObject}
     * @param attrSize The attribute size for each vertex.\
     * Meaning the number of components that make up a single value. Such as 2 for 2D data or 3 for 3D data.
     */
    public createAttribute(
        attribute: number,
        dataType: number,
        data: number[] | Float32Array | Int32Array,
        attrSize: 1 | 2 | 3 | 4
    ): VertexBufferObject {
        if (this.vertexBufferObjects.has(attribute)) {
            throw new Error(
                `Attribute ${attribute} is already in use. Attributes should be unique.`
            );
        }
        let buffer: ArrayBuffer | Float32Array | Int32Array;
        if (Array.isArray(data)) {
            switch (dataType) {
                case gl.FLOAT:
                    buffer = new Float32Array(data);
                    break;
                case gl.INT:
                    buffer = new Int32Array(data);
                    break;
                default:
                    throw new Error(
                        'Attempt to create attribute of unknown data type ' +
                            dataType.toString()
                    );
            }
        } else {
            buffer = data;
        }
        const vbo = new AttributeVertexBufferObject(attrSize, dataType);
        vbo.bind();
        vbo.storeData(buffer as unknown as ArrayBuffer);
        vbo.setPointer(attribute);
        vbo.unbind();
        this.vertexBufferObjects.set(attribute, vbo);
        return vbo;
    }

    /**
     * Method to bind the VAO and optionally enable vertex attribute arrays.
     * In order to access the attribute values during the rendering, the VertexAttribArray has to be enabled.
     * @param attributes Variadic parameter listing all the attributes that are to be enabled.\
     * Index values start at 0 and increase by 1 for each consecutive attribute.
     * @returns True to indicate that the {@link VertexArrayObject} was successfully bound and requested attributes enabled.
     */
    public bind(...attributes: number[]): boolean {
        gl.bindVertexArray(this.id);
        attributes.forEach((attribute) => {
            gl.enableVertexAttribArray(attribute);
        });
        return true;
    }

    /**
     * Method to update the contents of a {@link AttributeVertexBufferObject} stored inside this {@link VertexArrayObject}.\
     * User is responsible for verifying the provided {@link data} is valid for the {@link VertexBufferObject}.\
     * User should also make sure this VertexArrayObject is {@link bind bound} when calling this method.\
     * @throws Error if the {@link attribute} is not currently defined or\
     * {@link data} is array of numbers and {@link AttributeVertexBufferObject.dataType dataType} is of unknown type.
     * @param attribute The attribute whose data should be changed.
     * @param data The data to be stored inside the {@link AttributeVertexBufferObject}.
     * @returns Reference to the {@link AttributeVertexBufferObject} whose data was modified.
     */
    public setData(
        attribute: number,
        data: number[] | Float32Array | Int32Array
    ): AttributeVertexBufferObject {
        const vbo = this.vertexBufferObjects.get(attribute);
        if (!vbo) {
            throw new Error(
                `Attempt to setData of attribute ${attribute} on VAO that doesn't have such mapping`
            );
        }
        let buffer: ArrayBuffer | Float32Array | Int32Array;
        if (Array.isArray(data)) {
            switch (vbo.dataType) {
                case gl.FLOAT:
                    buffer = new Float32Array(data);
                    break;
                case gl.INT:
                    buffer = new Int32Array(data);
                    break;
                default:
                    throw new Error(
                        'Attempting to create attribute of unknown data type ' +
                            vbo.dataType.toString()
                    );
            }
        } else {
            buffer = data;
        }
        vbo.bind();
        vbo.storeData(buffer as unknown as ArrayBuffer);
        vbo.setPointer(attribute);
        vbo.unbind();
        return vbo;
    }

    /**
     * Method to unbind the VAO and optionally disable vertex attribute arrays.
     * It is adviced to disable the enabled attributes when those are no longer needed to prevent unintentional modifications.
     * @param attributes Variadic parameter listing all the attributes that are to be disabled.\
     * Index values start at 0 and increase by 1 for each consecutive attribute.
     * @returns True to indicate that the {@link VertexArrayObject} was successfully unbound and requested attributes disabled.
     */
    public unbind(...attributes: number[]): boolean {
        attributes.forEach((attribute) => {
            gl.disableVertexAttribArray(attribute);
        });
        gl.bindVertexArray(null);
        return true;
    }

    /**
     * Method to delete this {@link VertexArrayObject} and all the {@link VertexBufferObject VertexBufferObjects} contained within.
     * @returns True to indicate that the VAO and it's contents were successfully deleted.
     */
    public delete(): boolean {
        gl.deleteVertexArray(this.id);
        this.deleteContent();
        return true;
    }

    /**
     * Method to delete contents of this {@link VertexArrayObject}.\
     * This will delete the {@link indexVBO} and all the {@link vertexBufferObjects attributeVBOs}.
     * @returns True to indicate that the VAO's contents were successfully deleted.
     */
    public deleteContent(): boolean {
        this.vertexBufferObjects.forEach((vbo) => {
            vbo.delete();
        });
        if (this.indexVBO) {
            this.indexVBO.delete();
            this._indexCount = 0;
        }
        return true;
    }
}
